import React from 'react';
import styles from './maximized-player.module.css';
import { TrackQueue } from './track-queue';

const Icon = ({ name, className }) => {
  const p = { className: className || styles.icon, viewBox: '0 0 24 24', fill: 'currentColor' };
  switch (name) {
    case 'play': return <svg {...p}><polygon points="8,5 8,19 19,12" /></svg>;
    case 'pause': return <svg {...p}><rect x="6" y="5" width="4" height="14"/><rect x="14" y="5" width="4" height="14"/></svg>;
    case 'prev': return <svg {...p}><rect x="5" y="5" width="2" height="14"/><polygon points="19,5 9,12 19,19"/></svg>;
    case 'next': return <svg {...p}><rect x="17" y="5" width="2" height="14"/><polygon points="5,5 15,12 5,19"/></svg>;
    case 'shuffle': return <svg {...p}><path d="M4 7h2c2 0 3 1 4 2l2 2c1 1 2 2 4 2h4v2h-4c-2 0-3-1-4-2l-2-2c-1-1-2-2-4-2H4V7zM18 7h4v2h-4c-1 0-2 .5-3 1.5l-1 1 1 1c1 1 2 1.5 3 1.5h4v2h-4c-2 0-3-.7-4-1.8l-2-2.2 2-2.2C15 7.7 16 7 18 7zM4 15h2c2 0 3-.7 4-1.8l.3-.4-1.6-1.6-.3.4C7.5 12.5 7 13 6 13H4v2z"/></svg>;
    case 'repeat': return <svg {...p}><path d="M7 7h8a4 4 0 0 1 4 4v1h-2v-1a2 2 0 0 0-2-2H7v3L3 8l4-4v3zM17 17H9a4 4 0 0 1-4-4v-1h2v1a2 2 0 0 0 2 2h8v-3l4 4-4 4v-3z"/></svg>;
    case 'repeat1': return <svg {...p}><path d="M7 7h8a4 4 0 0 1 4 4v1h-2v-1a2 2 0 0 0-2-2H7v3L3 8l4-4v3zM17 17H9a4 4 0 0 1-4-4v-1h2v1a2 2 0 0 0 2 2h8v-3l4 4-4 4v-3z"/><text x="12" y="16" textAnchor="middle" fontSize="8" fill="currentColor">1</text></svg>;
    case 'vol': return <svg {...p}><path d="M5 10v4h3l4 3V7l-4 3H5z"/><path d="M16 8a5 5 0 0 1 0 8" stroke="currentColor" strokeWidth="2" fill="none"/></svg>;
    case 'volx': return <svg {...p}><path d="M5 10v4h3l4 3V7l-4 3H5z"/><path d="M18 9l-6 6M12 9l6 6" stroke="currentColor" strokeWidth="2" fill="none"/></svg>;
    case 'minimize': return <svg {...p}><path d="M9 5H5v4H3V3h6v2zM15 5h4v4h2V3h-6v2zM9 19H5v-4H3v6h6v-2zM19 15v4h-4v2h6v-6h-2z"/></svg>;
    case 'close': return <svg {...p}><path d="M6 6l12 12M6 18L18 6" stroke="currentColor" strokeWidth="2" fill="none"/></svg>;
    default: return null;
  }
};

export function MaximizedPlayer({
  currentTrack,
  isPlaying,
  isLoading,
  error,
  volume,
  isMuted,
  repeatMode,
  isShuffled,
  progress,
  duration,
  currentTime,
  canPlay,
  tracks,
  currentTrackIndex,
  onTogglePlayPause,
  onNext,
  onPrevious,
  onSeek,
  onVolumeChange,
  onToggleMute,
  onToggleRepeat,
  onToggleShuffle,
  onToggleMaximize,
  onTrackSelect
}) {
  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleProgressClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const percentage = ((e.clientX - rect.left) / rect.width) * 100;
    onSeek(Math.max(0, Math.min(100, percentage)));
  };

  const RepeatIcon = repeatMode === 'one' ? 'repeat1' : 'repeat';

  if (error) {
    return (
      <div className={styles.root}>
        <div className={styles.header}>
          <h1 className={styles.h1}>qpcore beats</h1>
          <button onClick={onToggleMaximize} className={styles.iconBtn} aria-label="Close player">
            <Icon name="close" />
          </button>
        </div>

        <div className={styles.errorCenter}>
          <div className={styles.errorBadge}>!</div>
          <h3 className={styles.errorTitle}>Playback Error</h3>
          <p className={styles.errorSub}>{error}</p>
          <button onClick={onToggleMaximize} className={styles.outlineBtn}>Close Player</button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <h1 className={styles.h1}>qp Player</h1>
        <button onClick={onToggleMaximize} className={styles.iconBtn} aria-label="Minimize">
          <Icon name="minimize" />
        </button>
      </div>

      <div className={styles.main}>
        {/* Artwork & info */}
        <div className={styles.center}>
          <div
            className={styles.artwork}
            style={{ backgroundImage: currentTrack?.cover ? `url(${currentTrack.cover})` : 'none' }}
          >
            {!currentTrack?.cover && <span className={styles.artNote}>♪</span>}
          </div>

          <div className={styles.info}>
            <h2 className={styles.title} title={currentTrack?.title || ''}>
              {currentTrack?.title || 'No track selected'}
            </h2>
            <p className={styles.artist} title={currentTrack?.artist || ''}>
              {currentTrack?.artist || 'Pick a beat to start'}
            </p>
            {currentTrack?.album && <p className={styles.album}>{currentTrack.album}</p>}

            {Array.isArray(currentTrack?.genres) && currentTrack.genres.length > 0 && (
              <div className={styles.tags}>
                {currentTrack.genres.map((g, i) => (
                  <span key={i} className={styles.tag}>{g}</span>
                ))}
              </div>
            )}

            <div className={styles.tech}>
              {currentTrack?.bitrate && <span>{currentTrack.bitrate}kbps</span>}
              {currentTrack?.sampleRate && <span>{currentTrack.sampleRate}kHz</span>}
              {currentTrack?.format && <span>{String(currentTrack.format).toUpperCase()}</span>}
            </div>
          </div>
        </div>

        {/* Controls & side */}
        <div className={styles.side}>
          {/* Progress */}
          <div className={styles.progressWrap} onClick={handleProgressClick}>
            <div className={styles.progressTrack}>
              <div className={styles.progressFill} style={{ width: `${progress || 0}%` }} />
              <div className={styles.progressThumb} style={{ left: `calc(${progress || 0}% - 8px)` }} />
            </div>
            <div className={styles.timeRow}>
              <span>{formatTime(currentTime)}</span>
              <span>{formatTime(duration)}</span>
            </div>
          </div>

          {/* Main controls */}
          <div className={styles.controlsRow}>
            <button onClick={onToggleShuffle} className={`${styles.iconBtn} ${isShuffled ? styles.active : ''}`} aria-label="Shuffle">
              <Icon name="shuffle" />
            </button>

            <button onClick={onPrevious} className={styles.iconBtn} aria-label="Previous">
              <Icon name="prev" className={styles.iconLg} />
            </button>

            <button
              onClick={onTogglePlayPause}
              disabled={isLoading || !canPlay}
              className={styles.bigBtn}
              aria-label={isPlaying ? 'Pause' : 'Play'}
            >
              {isLoading ? <span className={styles.spinner} /> : isPlaying ? <Icon name="pause" className={styles.iconLg} /> : <Icon name="play" className={styles.iconLg} />}
            </button>

            <button onClick={onNext} className={styles.iconBtn} aria-label="Next">
              <Icon name="next" className={styles.iconLg} />
            </button>

            <button onClick={onToggleRepeat} className={`${styles.iconBtn} ${repeatMode !== 'none' ? styles.active : ''}`} aria-label="Repeat">
              <Icon name={RepeatIcon} />
            </button>
          </div>

          {/* Volume */}
          <div className={styles.volumeRow}>
            <button onClick={onToggleMute} className={styles.iconBtn} aria-label="Mute">
              {(isMuted || volume === 0) ? <Icon name="volx" /> : <Icon name="vol" />}
            </button>
            <input
              type="range" min="0" max="100" step="1"
              value={isMuted ? 0 : Math.round((volume || 0) * 100)}
              onChange={(e) => onVolumeChange(Number(e.target.value) / 100)}
              className={styles.range}
            />
            <span className={styles.volPct}>{Math.round((isMuted ? 0 : volume) * 100)}%</span>
          </div>

          {/* Now playing */}
          <div className={styles.nowPlaying}>
            <div className={styles.nowRow}>
              <span>Quality:</span>
              <span className={styles.nowVal}>
                {(currentTrack?.format ? String(currentTrack.format).toUpperCase() : 'Unknown')} • {currentTrack?.bitrate || '---'}kbps
              </span>
            </div>
            <div className={styles.nowRow}>
              <span>Sample Rate:</span>
              <span className={styles.nowVal}>{currentTrack?.sampleRate || '---'}kHz</span>
            </div>
            <div className={styles.nowRow}>
              <span>Status:</span>
              <span className={canPlay ? styles.ok : styles.warn}>
                {isLoading ? 'Loading...' : canPlay ? 'Ready' : 'Buffering'}
              </span>
            </div>
          </div>

          {/* Queue */}
          <div className={styles.queueWrap}>
            <TrackQueue
              tracks={tracks}
              currentTrackIndex={currentTrackIndex}
              onTrackSelect={onTrackSelect}
              maxVisible={5}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

